import AxiosInstance from "./Axios";
import Cookies from "universal-cookie";

// --------------------------------------------------------
// ENDPOINTS
// --------------------------------------------------------
const API_CLIENT_BASE = "/admin/client";
const API_ADMIN_WALLET = "/admin/wallet";

// --------------------------------------------------------
// HELPERS
// --------------------------------------------------------
const TIMEOUT = 20000;

function authHeaders(extraHeaders = {}) {
  const cookies = new Cookies();
  const token = cookies.get("token");

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
}

function buildAxiosError(error, url) {
  if (error?.response) {
    return new Error(
      `HTTP ${error.response.status}: ${JSON.stringify(
        error.response.data
      )} | URL: ${url}`
    );
  }

  return new Error(`HTTP N/A: ${error?.message || "Network Error"} | URL: ${url}`);
}

function toUnixUtcString(value, fallbackSeconds) {
  if (value === null || value === undefined || value === "") {
    return String(fallbackSeconds);
  }

  const asNumber = Number(value);
  if (Number.isFinite(asNumber)) {
    return String(Math.floor(asNumber));
  }

  const parsed = new Date(value);
  const ms = parsed.getTime();
  if (Number.isFinite(ms)) {
    return String(Math.floor(ms / 1000));
  }

  return String(fallbackSeconds);
}

// --------------------------------------------------------
// GET CLIENT BY EMAIL
// --------------------------------------------------------
export async function getClientByEmail(email, extraHeaders = {}) {
  const cleanEmail = String(email ?? "").trim();

  if (!cleanEmail) {
    throw new Error("Missing required parameter: email");
  }

  const encodedEmail = encodeURIComponent(cleanEmail);
  const url = `${API_CLIENT_BASE}/${encodedEmail}`;

  try {
    const res = await AxiosInstance.get(url, {
      headers: authHeaders(extraHeaders),
      timeout: TIMEOUT,
      validateStatus: () => true,
    });

    if (res.status >= 200 && res.status < 300) {
      const rawPayload = res.data?.data ?? res.data ?? {};

      const clientInfo = typeof rawPayload === "object" ? rawPayload : {};
      const wallet = clientInfo.wallet ?? {};

      return {
        data: {
          ...clientInfo,
          name: clientInfo.name || clientInfo.full_name || clientInfo.email || cleanEmail,
          balance: Number(wallet.balance ?? clientInfo.balance ?? 0),
          currency: wallet.currency || clientInfo.currency || "",
        },
        httpStatus: res.status,
      };
    }

    throw new Error(`HTTP ${res.status}: ${JSON.stringify(res.data)} | URL: ${url}`);
  } catch (error) {
    if (!error?.response && String(error?.message ?? "").includes("| URL:")) {
      throw error;
    }
    throw buildAxiosError(error, url);
  }
}

// --------------------------------------------------------
// GET WALLET TRANSACTIONS
// --------------------------------------------------------
export async function getWalletTransactions(email, params = {}, extraHeaders = {}) {
  const cleanEmail = String(email ?? "").trim();

  if (!cleanEmail) {
    throw new Error("Missing required parameter: email");
  }

  const url = API_ADMIN_WALLET;
  const nowUtcUnix = Math.floor(Date.now() / 1000);
  const query = {
    email: cleanEmail,
    from: toUnixUtcString(params?.from, 0),
    to: toUnixUtcString(params?.to, nowUtcUnix),
  };

  try {
    const transactionsRes = await AxiosInstance.get(url, {
      params: query,
      headers: authHeaders(extraHeaders),
      timeout: TIMEOUT,
      validateStatus: () => true,
    });

    if (transactionsRes.status >= 200 && transactionsRes.status < 300) {
      const rawPayload =
        transactionsRes?.data?.data ?? transactionsRes?.data ?? {};
      const history = Array.isArray(rawPayload?.history)
        ? rawPayload.history
        : Array.isArray(rawPayload?.transactions)
          ? rawPayload.transactions
          : [];
      const user = rawPayload?.user ?? null;

      return {
        data: {
          ...rawPayload,
          user:
            user ??
            {
              name: cleanEmail,
              email: cleanEmail,
              wallet: {
                balance: 0,
                currency: "",
              },
            },
          history,
        },
        httpStatus: transactionsRes.status,
      };
    }

    throw new Error(
      `HTTP ${transactionsRes.status}: ${JSON.stringify(
        transactionsRes.data
      )} | URL: ${url}`
    );
  } catch (error) {
    if (!error?.response && String(error?.message ?? "").includes("| URL:")) {
      throw error;
    }
    throw buildAxiosError(error, url);
  }
}

// --------------------------------------------------------
// EXPORT DEFAULT
// --------------------------------------------------------
const walletService = {
  getClientByEmail,
  getWalletTransactions,
};

export default walletService;
